/**
 * Runtime platform detection — used to decide auth flow and API headers.
 *
 * Three contexts are supported:
 *   1. Telegram WebApp (window.Telegram.WebApp.initData present)
 *   2. Max WebApp (window.WebApp or WebAppData in location hash)
 *   3. Standalone web (Telegram Login Widget params stored in localStorage)
 */

export type Platform = 'telegram-webapp' | 'max-webapp' | 'web-login-widget' | 'unknown';

declare global {
  interface Window {
    WebApp?: {
      initData?: string;
      initDataUnsafe?: unknown;
    };
  }
}

const HASH_INIT_KEY = 'WebAppData';
const SESSION_INIT_KEY = '__max_init_data';

/**
 * Pull the raw Max WebAppData string (exactly as sent in the URL fragment) from either
 * a live location.hash or a cached sessionStorage entry. Max miniapps deliver init data
 * once in the fragment, so we stash it on first read to survive client-side navigation.
 */
export const readMaxInitDataRaw = (): string | null => {
  if (typeof window === 'undefined') return null;

  const sessionValue = window.sessionStorage?.getItem(SESSION_INIT_KEY);
  if (sessionValue) return sessionValue;

  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;

  const hashParams = new URLSearchParams(hash);
  const raw = hashParams.get(HASH_INIT_KEY);
  if (raw) {
    window.sessionStorage?.setItem(SESSION_INIT_KEY, raw);
    return raw;
  }

  // Some hosts pass just the raw WebAppData body without the `WebAppData=` key.
  if (hash.includes('hash=') && hash.includes('auth_date=')) {
    window.sessionStorage?.setItem(SESSION_INIT_KEY, hash);
    return hash;
  }

  return null;
};

export const isTelegramWebApp = (): boolean =>
  typeof window !== 'undefined' && !!window.Telegram?.WebApp?.initData;

export const isMaxWebApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.WebApp?.initData) return true;
  return readMaxInitDataRaw() !== null;
};

export const detectPlatform = (): Platform => {
  if (isTelegramWebApp()) return 'telegram-webapp';
  if (isMaxWebApp()) return 'max-webapp';
  if (typeof window !== 'undefined' && window.localStorage?.getItem('telegram_login_widget_data')) {
    return 'web-login-widget';
  }
  return 'unknown';
};

/**
 * Parse the embedded `user` JSON from Max init data (without verifying the hash — server-side
 * remains the source of truth). Returns null when init data is missing or malformed.
 */
export const parseMaxInitUser = (): {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
} | null => {
  const raw = window.WebApp?.initData || readMaxInitDataRaw();
  if (!raw) return null;

  const params = new URLSearchParams(raw);
  const userParam = params.get('user');
  if (!userParam) return null;

  try {
    const parsed = JSON.parse(userParam);
    if (parsed && typeof parsed.id === 'number') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};
